import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { ReactComponent as CadreLogo } from './cadre-logo.svg';

// Served from public/ under a stable name so index.html can preload it.
const HERO_URL = `${process.env.PUBLIC_URL}/hero.webp`;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusable(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.closest('[aria-hidden="true"]')
  );
}

function trackEvent(eventName, params) {
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
}

function App() {
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Honeypot: hidden from people, filled in by bots. Checked server-side.
  const [website, setWebsite] = useState('');

  const signupBtnRef = useRef(null);
  const dialogRef = useRef(null);
  const submittedRef = useRef(false);

  const openModal = () => {
    setModalOpen(true);
    trackEvent('signup_modal_open');
  };

  const closeModal = useCallback(() => {
    if (!submittedRef.current) {
      trackEvent('signup_modal_close');
    }
    submittedRef.current = false;
    setModalOpen(false);
    setSubmitted(false);
    setError('');
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const trigger = signupBtnRef.current;
    const dialog = dialogRef.current;
    const nodes = getFocusable(dialog);
    const firstField = nodes.find((el) => el.tagName === 'INPUT');
    (firstField || nodes[0] || dialog)?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key !== 'Tab') return;

      const list = getFocusable(dialogRef.current);
      if (list.length === 0) {
        e.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (e) => {
      const node = dialogRef.current;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        const list = getFocusable(node);
        (list[0] || node).focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      trigger?.focus();
    };
  }, [modalOpen, closeModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');
    trackEvent('signup_submit', { has_name: Boolean(name.trim()) });

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, name, website }),
      });

      const data = await response.json();

      if (response.ok) {
        trackEvent('sign_up', { method: 'email' });
        submittedRef.current = true;
        setSubmitted(true);
        setName('');
        setEmail('');
      } else {
        trackEvent('signup_error', {
          error_type: response.status === 429 ? 'rate_limit' : 'api',
        });
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      trackEvent('signup_error', {
        error_type: err instanceof TypeError ? 'network' : 'unknown',
      });
      setError('Failed to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const titleId = submitted ? 'signup-modal-success' : 'signup-modal-title';

  return (
    <div className="App">
      <div className="landing-container" style={{ backgroundImage: `url(${HERO_URL})` }}>
        <div className="background-overlay"></div>

        <header className="top-bar">
          <CadreLogo className="logo" />
        </header>

        <div className="hero">
          <h2 className="hero-headline">
            PREMIUM SPORTS BEVERAGE
          </h2>
        </div>

        <footer className="bottom-bar">
          <a
            href="https://www.instagram.com/drinkcadre/"
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-link"
          >
            @DRINKCADRE
          </a>
          <p className="tagline">NEW FUEL FOR A NEW PHASE.</p>
          <button
            ref={signupBtnRef}
            className="signup-btn"
            onClick={openModal}
          >
            SIGN UP
          </button>
        </footer>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            ref={dialogRef}
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>

            <div className="modal-brand">
              <div className="brand-lines">
                <div className="red-line"></div>
                <div className="red-line"></div>
              </div>
              <CadreLogo className="modal-logo" />
              <div className="brand-lines">
                <div className="red-line"></div>
                <div className="red-line"></div>
              </div>
            </div>

            {!submitted ? (
              <>
                <p id="signup-modal-title" className="modal-text">
                  JOIN OUR MAILING LIST
                  FOR EXCLUSIVE INFO
                  AND LIMITED LAUNCHES
                </p>

                <form className="modal-form" onSubmit={handleSubmit}>
                  <div className="hp-field" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="signup-name" className="modal-label">Name</label>
                    <input
                      id="signup-name"
                      name="name"
                      type="text"
                      className="modal-input"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="signup-email" className="modal-label">Email</label>
                    <input
                      id="signup-email"
                      name="email"
                      type="email"
                      className="modal-input"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="send-btn" disabled={loading}>
                    {loading ? 'SENDING...' : 'SEND'}
                  </button>
                </form>

                {error && <p className="error-message" role="alert">{error}</p>}
              </>
            ) : (
              <div className="success-content">
                <p id="signup-modal-success" className="success-message">
                  Thank you for joining.<br />We'll be in touch soon.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
